/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as React from 'react';

import Downshift from 'downshift';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import * as JsSearch from 'js-search';
import _ from 'lodash';

import { Highlighter, StyleSheet, SvgButton, color, css, spacing, zIndex } from '@coursera/coursera-ui';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import toolbarTableStyles from '@coursera/coursera-ui/lib/components/extended/TableV2/ToolbarTable/ToolbarTableStyles';
import { SvgClose, SvgSearch } from '@coursera/coursera-ui/svg';

const styles = StyleSheet.create({
  TypeAheadV2: {
    position: 'relative',
    background: color.white,
  },
  selectItem: {
    display: 'inline-block',
    ':hover': {
      cursor: 'pointer',
      backgroundColor: color.primary,
      color: color.white,
    },
  },
  input: {
    flex: 1,
    height: 48,
    padding: spacing.sm,
    border: `1px solid ${color.darkGray}`,
    ':focus': {
      'border-color': color.primary,
      outline: 'none',
    },
    ':disabled': {
      'background-color': color.disabled,
    },
  },
  dropDown: {
    position: 'absolute',
    width: '100%',
    zIndex: zIndex.lg,
  },
});

type Item = { [key: string]: string };

export type TypeaheadV3Props = {
  itemToString?: (item: Item) => string;
  maxItemsCount?: number;
  onInputChange?: (search: string) => void;
  onSearch?: (search: string) => void;
  searchDocuments?: Array<Item>;
  searchIndexFields?: Array<string>;
  /**
   * uid field for js-search
   */
  searchUid?: string;
  defaultValue?: string;
  minSearchStrLength?: number;
  dropDownStyle?: React.CSSProperties;
  inputClassName?: string;
  placeholder?: string;
};

type States = {
  inputValue: string;
  menuIsOpen: boolean;
};

/**
 * A simple TypeAhead/Autosuggest component based on Downshift
 * Benefit:
 * - Simple API. Previous TypeAhead has lots of apis which is unnecessary and hard to understand
 * - CUI styles for button and input, no external css dependency
 * - Encapsulate our own usage as defaults
 * - Introduce downshift which is a lot more powerful to build flexible and accessible enhanced input components
 * - Previous TypeAhead has some external dependencies which is hard to manager and it's breaking our production now
 *   The new implementation has only two dependencies: downshift and js-search
 */
export default class TypeAheadV2 extends React.Component<TypeaheadV3Props, States> {
  declare _search: JsSearch.Search;

  declare _itemToString: (item: Item) => string;

  static defaultProps = {
    searchUid: 'term',
    searchIndexFields: ['term'],
    searchDocuments: [],
    maxItemsCount: 5,
    defaultValue: '',
    minSearchStrLength: 0,
  };

  constructor(props: TypeaheadV3Props, context: {}) {
    super(props, context);
    const { searchUid, searchIndexFields, searchDocuments, itemToString } = props;
    // Set up search strategy, add more strategies in the future if needed
    const search = new JsSearch.Search(searchUid);
    search.indexStrategy = new JsSearch.AllSubstringsIndexStrategy();
    searchIndexFields!.forEach((field) => {
      search.addIndex(field);
    });
    search.addDocuments(searchDocuments);
    this._search = search;
    this._itemToString = itemToString!;
    if (!itemToString) {
      this._itemToString = (item) => item[searchUid!];
    }
    this.state = {
      inputValue: props.defaultValue || '',
      menuIsOpen: false,
    };
  }

  handleStateChange = _.debounce(
    ({ inputValue }) => {
      this.setMenuIsOpen(true);
      if (this.props.onInputChange) {
        this.props.onInputChange(inputValue);
      }
      return inputValue !== undefined && this.setState({ inputValue });
    },
    0.3,
    {
      leading: true,
      trailing: false,
    }
  );

  handleSearch = () => {
    this.setMenuIsOpen(false);
    if (this.props.onSearch) {
      this.props.onSearch(this.state.inputValue);
    }
  };

  handleSelect = (selection: string) => {
    this.setMenuIsOpen(false);
    if (this.props.onSearch) {
      this.props.onSearch(selection);
    }
  };

  handleInputFocus = () => {
    this.setMenuIsOpen(true);
  };

  handleOuterClick = () => {
    this.setMenuIsOpen(false);
  };

  handleInputKeyUp = (e: React.KeyboardEvent) => {
    if (e.keyCode === 13) {
      // Manually trigger search if 'enter' key press is detected
      this.handleSearch();
    }
  };

  setMenuIsOpen = (menuIsOpen: boolean) => {
    if (menuIsOpen !== this.state.menuIsOpen) {
      this.setState({ menuIsOpen });
    }
  };

  render() {
    const { maxItemsCount, searchUid, minSearchStrLength, dropDownStyle, inputClassName, placeholder } = this.props;
    const { inputValue, menuIsOpen } = this.state;
    const dropDownBackgroundColor = dropDownStyle ? dropDownStyle.backgroundColor : color.white;
    const dropDownColor = dropDownStyle ? dropDownStyle.color : color.primary;
    const downshiftId = `typeaheadv3-${searchUid}`;

    return (
      <div {...css(styles.TypeAheadV2)}>
        <Downshift
          id={downshiftId}
          isOpen={menuIsOpen}
          onOuterClick={this.handleOuterClick}
          onStateChange={this.handleStateChange}
          selectedItem={inputValue}
          onChange={this.handleSelect}
        >
          {({
            getInputProps,
            getItemProps,
            isOpen,
            inputValue: downshiftInputValue,
            highlightedIndex,
            selectedItem,
          }) => (
            <div>
              <div {...css(toolbarTableStyles.searchBarWrapper, menuIsOpen && toolbarTableStyles.focused)}>
                <div {...css(toolbarTableStyles.searchBarIconWrapper, toolbarTableStyles.iconWrapper)}>
                  <SvgSearch size={18} color="#8E8E93" />
                </div>
                <input
                  {...getInputProps({ id: `${downshiftId}-input` })}
                  {...css(inputClassName, toolbarTableStyles.searchBarInput)}
                  onFocus={this.handleInputFocus}
                  onKeyUp={this.handleInputKeyUp}
                  placeholder={placeholder}
                />
                <SvgButton
                  size="sm"
                  type="icon"
                  style={{ display: downshiftInputValue ? 'flex' : 'none' }}
                  htmlAttributes={{ tabIndex: 0 }}
                  onClick={() => {
                    this.handleStateChange({ inputValue: '' });
                  }}
                  svgElement={<SvgClose size={15} hoverColor={color.danger} />}
                />
              </div>
              {isOpen &&
              downshiftInputValue !== null &&
              minSearchStrLength !== undefined &&
              downshiftInputValue.length >= minSearchStrLength ? (
                <div {...css(styles.dropDown)}>
                  {this._search
                    .search(downshiftInputValue)
                    .slice(0, maxItemsCount)
                    .map((item: Item, index: number | null) => (
                      <div
                        {...getItemProps({
                          key: item[searchUid!],
                          index: index ?? undefined,
                          item: item[searchUid!],
                          style: {
                            fontWeight: selectedItem === item ? 'bold' : 'normal',
                            ...dropDownStyle,
                            backgroundColor: highlightedIndex === index ? color.primary : dropDownBackgroundColor,
                            color: highlightedIndex === index ? color.white : dropDownColor,
                          },
                        })}
                      >
                        <div {...css('p-a-1 border-a w-100', styles.selectItem)}>
                          <Highlighter search={downshiftInputValue} text={this._itemToString(item)} />
                        </div>
                      </div>
                    ))}
                </div>
              ) : null}
            </div>
          )}
        </Downshift>
      </div>
    );
  }
}
